import { fetchJson } from '@atlassian/jira-fetch/src/index.tsx';
import {
	performPostRequest,
	performDeleteRequest,
} from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type {
	ConnectionStatusResponse,
	ConnectionsBulkRequestInput,
	ConnectionsBulkResponse,
	ConnectionsRequestInput,
	ConnectionsResponse,
} from './types.tsx';

const CONNECTIONS_BULK_API_URL = '/rest/polaris/v2/issue-connections/bulk';

export const createConnectionsBulk = (connections: ConnectionsBulkRequestInput) =>
	performPostRequest<ConnectionsBulkResponse>(CONNECTIONS_BULK_API_URL, {
		body: JSON.stringify({
			connections,
		}),
	});

export const deleteConnectionsBulk = (connections: ConnectionsBulkRequestInput) =>
	performDeleteRequest<ConnectionsBulkResponse>(CONNECTIONS_BULK_API_URL, {
		body: JSON.stringify({
			connections,
		}),
	});

export const getConnectionBulkStatus = (taskId: string) =>
	fetchJson<ConnectionStatusResponse>(`/rest/polaris/v2/issue-connections/${taskId}/status`);

// TODO: https://pi-dev-sandbox.atlassian.net/browse/POL-13529 - replace with API for 1-to-many connections create as a single request
export const createConnections = (
	connection: ConnectionsRequestInput,
): Promise<ConnectionsResponse> =>
	createConnectionsBulk([connection]).then(waitForBulkConnectionComplete);

// TODO: https://pi-dev-sandbox.atlassian.net/browse/POL-13529 - replace with API for 1-to-many connections delete as a single request
export const deleteConnections = (
	connection: ConnectionsRequestInput,
): Promise<ConnectionsResponse> =>
	deleteConnectionsBulk([connection]).then(waitForBulkConnectionComplete);

// This is a temporary solution before there is an API for updating 1-to-many connections as a single request without polling the status, will be cleaned up during https://pi-dev-sandbox.atlassian.net/browse/POL-13529
const waitForBulkConnectionComplete = ({ taskId }: ConnectionsBulkResponse) => {
	const STATUS_CHECK_INTERVAL = 2000;
	const MAX_RETRIES = 15;

	return new Promise<ConnectionsResponse>((resolve, reject) => {
		let retryCount = 0;
		const interval = setInterval(() => {
			retryCount += 1;
			if (retryCount > MAX_RETRIES) {
				clearInterval(interval);
				resolve({ failures: [] });
				return;
			}
			getConnectionBulkStatus(taskId)
				.then(({ failures, progress }) => {
					if (progress === 100) {
						clearInterval(interval);

						if (failures.length > 0) {
							resolve({ failures: failures[0].issueToFailures });
						} else {
							resolve({ failures: [] });
						}
					}
				})
				.catch((error) => {
					// Only reject in case getConnectionBulkStatus is failing on the last call, otherwise
					// we will keep retrying anyways either for failed requests or successful but with progress < 100
					if (retryCount === MAX_RETRIES) {
						clearInterval(interval);
						reject(error);
					}
				});
		}, STATUS_CHECK_INTERVAL);
	});
};
